import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ConfigurationService } from '../../services/configuration.service';
import { AuthService } from '../../services/auth.service';
import { AppUrls } from '../../app-urls';
import { daysOfTheWeek } from '../../constants';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { distinctUntilChanged, first, tap } from 'rxjs/operators';
import { ParticipantInfo } from 'src/app/generated/participant/participant-info';
import { ParticipantService } from 'src/app/supplier/services/participant.service';
import { Moment } from 'moment';
import { LinkTypeEnum } from '../../enums/link-type.enum';
import { Utilities } from '../../utilities';

@Component({
  selector: 'app-customer-header',
  templateUrl: './customer-header.component.html',
  styleUrls: ['./customer-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerHeaderComponent implements OnInit, OnDestroy {
  public serverDateTime: Moment;
  public userInfo: ParticipantInfo;
  public userName: string = '';
  public urls = {
    publicUrl: `${this._configurationService.configuration.links.fcsPublicUrl}`,
    employeeView: `${this._configurationService.configuration.links.fcsBaseUrl}${AppUrls.headerUrls.employeeViewUrl}`,
    organizationView: `${this._configurationService.configuration.links.fcsBaseUrl}${AppUrls.headerUrls.organizationViewUrl}`,
    poaRegistry: `${this._configurationService.configuration.links.tenderPlatformUiUrl}/customer/lk/poa-registry`,
  };

  public readonly linkTypeEnum = LinkTypeEnum;

  constructor(
    private _configurationService: ConfigurationService,
    private _authService: AuthService,
    private _participantService: ParticipantService,
    private _cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this._configurationService.serverDateTimeBehaviorSubject
      .pipe(
        distinctUntilChanged((prev, curr) => prev.minute() === curr.minute()),
        tap((serverDateTime) => {
          this.serverDateTime = serverDateTime;
          this._cdr.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe();
    this._getParticipantInfo();
  }

  public serverDateString(): string {
    if (!this.serverDateTime) return '';

    const day = this._formatWithLeadingZero(this.serverDateTime.date());
    const month = this._formatWithLeadingZero(this.serverDateTime.month() + 1);
    const year = this.serverDateTime.year();
    const dayOfWeek = daysOfTheWeek[this.serverDateTime.day()];

    return `${dayOfWeek} ${day}.${month}.${year} г.`;
  }

  public serverHoursString(): string {
    return this._formatTimeUnit(this.serverDateTime?.hour());
  }

  public serverMinutesString(): string {
    return this._formatTimeUnit(this.serverDateTime?.minute());
  }

  public signOut(): void {
    this._authService.signOut('', this.userInfo);
  }

  private _formatWithLeadingZero(value: number): string {
    return value < 10 ? '0' + value : value.toString();
  }

  private _formatTimeUnit(value: number | null | undefined): string {
    if (Utilities.isNullOrUndefined(value)) return '';
    return value < 10 ? '0' + value : value.toString();
  }

  private _getParticipantInfo(): void {
    this._participantService
      .getParticipantInfo()
      .pipe(
        first((userInfo) => !!userInfo),
        tap((userInfo) => {
          this.userInfo = userInfo;
          if (userInfo.personInfo) {
            const { lastName, firstName, middleName } = this.userInfo.personInfo;
            this.userName = `${lastName || ''} ${firstName || ''} ${middleName || ''}`.trim();
          }
          this._cdr.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  public ngOnDestroy(): void {}
}
